import { useState } from "react";
import { Field as BaseField, List } from "rc-field-form";
import { Col, InputGroup, InputGroupText, Row, Tooltip, UncontrolledTooltip } from "reactstrap";
import { Disclosure } from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faPercent, faPlus, faTrash, faSortUp, faSortDown } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import ResourceSelect from "../../../../../../components/Inputs/ResourceSelect";
import Select from "rc-select";
import Field from "../../../../../../components/Field";
import Button from "../../../../../../components/Button";
import MoneyInput from "../../../../../../components/Inputs/MoneyInput";
import Text from "../../../../../../components/Inputs/Text";
import {
    getPathWithParams,
    getSettingsOrgResourceSearchFilters,
    getSettingsResourceSearchFilters,
    getSettingsResourceSearchFiltersWithLookups,
    renderOrgLevelResourceLabel,
    renderSettingsResourceLabel,
} from "../../../../../../utils/helpers/settings";
import { payCodeCalcFields } from "../../../CalculationGroup/helper";

const getWorkingShiftSearchFilters = (search) => {
    if (!search) {
        return;
    }
    return getSettingsResourceSearchFiltersWithLookups(search);
};

const ShiftRules = ({ rule, removeRule, moveRule, rulesCount, form, conflictMap, groups, ruleIndex }) => {
    const { t } = useTranslation();
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const toggleTooltip = () => setTooltipOpen((prev) => !prev);

    return (
        <Disclosure defaultOpen={true}>
            {({ open }) => (
                <>
                    <div
                        className={classNames(
                            "shift-rule-form__rules__item__head",
                            open && "shift-rule-form__rules__item__head--open"
                        )}
                    >
                        <BaseField name={rule.name}>
                            {({ value }) => (
                                <>
                                    <div className="w-75">
                                        <Disclosure.Button
                                            id={`shift-rule-toggle-${ruleIndex}`}
                                            className="d-inline-flex align-items-center p-0 mr-2 border-0 bg-transparent cursor-pointer"
                                        >
                                            <FontAwesomeIcon
                                                className="font-weight-700"
                                                icon={open ? faChevronUp : faChevronDown}
                                            />
                                        </Disclosure.Button>
                                        <Tooltip
                                            target={`shift-rule-toggle-${ruleIndex}`}
                                            isOpen={tooltipOpen}
                                            toggle={toggleTooltip}
                                            placement="top"
                                        >
                                            {open ? t("collapse") : t("expand")}
                                        </Tooltip>

                                        <span className="font-weight-700">
                                            {value?.shift
                                                ? renderSettingsResourceLabel(value?.shift)
                                                : t("no-working-shift")}
                                        </span>

                                        <span> | </span>

                                        <span>{value?.method ? t(value?.method) : t("no-method")}</span>
                                    </div>
                                    
                                    <div className="d-flex align-items-center gap-5">
                                        <span id={`shift-rule-sort-up-${ruleIndex}`}>
                                            <FontAwesomeIcon
                                                size="lg"
                                                className={classNames(
                                                    "shift-rule-form__sort-icon mt-2",
                                                    ruleIndex === 0 && "shift-rule-form__sort-icon--disabled"
                                                )}
                                                icon={faSortUp}
                                                onClick={() => ruleIndex > 0 && moveRule(ruleIndex, ruleIndex - 1)}
                                            />
                                        </span>
                                        <UncontrolledTooltip target={`shift-rule-sort-up-${ruleIndex}`} placement="top">
                                            {t("move-up")}
                                        </UncontrolledTooltip>

                                        <span id={`shift-rule-sort-down-${ruleIndex}`}>
                                            <FontAwesomeIcon
                                                size="lg"
                                                className={classNames(
                                                    "shift-rule-form__sort-icon mb-3",
                                                    ruleIndex === rulesCount - 1 && "shift-rule-form__sort-icon--disabled"
                                                )}
                                                icon={faSortDown}
                                                onClick={() => ruleIndex < rulesCount - 1 && moveRule(ruleIndex, ruleIndex + 1)}
                                            />
                                        </span>
                                        <UncontrolledTooltip target={`shift-rule-sort-down-${ruleIndex}`} placement="top">
                                            {t("move-down")}
                                        </UncontrolledTooltip>

                                        <span id={`shift-rule-delete-${ruleIndex}`}>
                                            <FontAwesomeIcon
                                                className="text-red cursor-pointer"
                                                icon={faTrash}
                                                onClick={() => removeRule(rule.name)}
                                            />
                                        </span>
                                        <UncontrolledTooltip target={`shift-rule-delete-${ruleIndex}`} placement="top">
                                            {t("delete-rule")}
                                        </UncontrolledTooltip>
                                    </div>
                                </>
                            )}
                        </BaseField>
                    </div>

                    <Disclosure.Panel className="shift-rule-form__rules__item__content">
                        <Row>
                            <Col md={6}>
                                {/* TODO: Do not allow shifts to conflict with each other in timeframes */}
                                <Field
                                    name={[rule.name, "shift"]}
                                    label={t("shift")}
                                    rules={[
                                        {
                                            required: true,
                                            message: t("shift-required"),
                                        },
                                    ]}
                                >
                                    <ResourceSelect
                                        resourcePath="/working-shift"
                                        renderLabel={renderSettingsResourceLabel}
                                        getSearchFilters={getWorkingShiftSearchFilters}
                                        placeholder={t("shift")}
                                        hasSearch
                                    />
                                </Field>
                            </Col>

                            <Col md={6}>
                                <Field
                                    name={[rule.name, "payCode"]}
                                    label={t("pay-code")}
                                    rules={[
                                        {
                                            required: true,
                                            message: t("pay-code-required"),
                                        },
                                    ]}
                                >
                                    <ResourceSelect
                                        resourcePath={getPathWithParams('/pay-code', true, {}, payCodeCalcFields, { hourType: 'unit' })}
                                        renderLabel={renderSettingsResourceLabel}
                                        getSearchFilters={getSettingsResourceSearchFilters}
                                        placeholder={t("pay-code")}
                                        hasSearch
                                    />
                                </Field>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Field
                                    name={[rule.name, "method"]}
                                    label={t("method")}
                                    rules={[
                                        {
                                            required: true,
                                            message: t("method-required"),
                                        },
                                    ]}
                                >
                                    <Select
                                        placeholder={t("method")}
                                        onChange={() => {
                                            form.resetFields([["rules", rule.name, "value"]]);
                                        }}
                                    >
                                        <Select.Option value="amount-per-hour">
                                            {t("amount-per-hour")}
                                        </Select.Option>
                                        <Select.Option value="amount-per-shift">
                                            {t("amount-per-shift")}
                                        </Select.Option>
                                        <Select.Option value="percentage">{t("percentage")}</Select.Option>
                                    </Select>
                                </Field>
                            </Col>

                            <Col md={6}>
                                <BaseField name={[rule.name, "method"]}>
                                    {({ value }) => {
                                        // TODO: validations and a proper percentage input
                                        switch (value) {
                                            case "amount-per-hour":
                                            case "amount-per-shift": {
                                                return (
                                                    <Field
                                                        name={[rule.name, "value"]}
                                                        label={t("value")}
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: t("value-required"),
                                                            },
                                                        ]}
                                                    >
                                                        <MoneyInput
                                                            placeholder={t("value")}
                                                            className="w-100 d-block"
                                                        />
                                                    </Field>
                                                );
                                            }
                                            case "percentage": {
                                                return (
                                                    <Field
                                                        name={[rule.name, "value"]}
                                                        label={t("value")}
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: t("value-required"),
                                                            },
                                                        ]}
                                                    >
                                                        {(field) => {
                                                            return (
                                                                <InputGroup>
                                                                    <InputGroupText>
                                                                        <FontAwesomeIcon className="px-1" icon={faPercent} />
                                                                    </InputGroupText>
                                                                    <Text type="number" placeholder={t("value")} min={1} {...field} />
                                                                </InputGroup>
                                                            );
                                                        }}
                                                    </Field>
                                                );
                                            }
                                            default: {
                                                return null;
                                            }
                                        }
                                    }}
                                </BaseField>
                            </Col>
                        </Row>

                        <List name={[rule.name, "conditions"]}>
                            {(conditions, { add: addCondition, remove: removeCondition }) => {
                                return (
                                    <div className="shift-rule-form__rules__item__content__conditions">
                                        {conditions.map((condition, conditionIndex) => {
                                            return (
                                                <div
                                                    key={condition.key}
                                                    className="shift-rule-form__rules__item__content__conditions__item"
                                                >
                                                    <Row>
                                                        <Col md={6}>
                                                            <Field
                                                                name={[condition.name, "resource"]}
                                                                label={t("resource")}
                                                                rules={[
                                                                    {
                                                                        required: true,
                                                                        message: t("resource-required"),
                                                                    },
                                                                ]}
                                                            >
                                                                <Select
                                                                    placeholder={t("resource")}
                                                                    onChange={() => {
                                                                        form.resetFields([
                                                                            [
                                                                                "rules",
                                                                                rule.name,
                                                                                "conditions",
                                                                                condition.name,
                                                                                "value",
                                                                            ],
                                                                        ]);
                                                                    }}
                                                                >
                                                                    <Select.Option value="job">
                                                                        {t("job")}
                                                                    </Select.Option>
                                                                    <Select.Option value="location">
                                                                        {t("location")}
                                                                    </Select.Option>
                                                                    <Select.Option value="union">
                                                                        {t("union")}
                                                                    </Select.Option>
                                                                    <Select.Option value="employee-type">
                                                                        {t("employee-type")}
                                                                    </Select.Option>
                                                                    <Select.Option value="employment-type">
                                                                        {t("employment-type")}
                                                                    </Select.Option>
                                                                    {groups.map((group) => (
                                                                        <Select.Option value={group.level}>
                                                                            {renderSettingsResourceLabel(group)}
                                                                        </Select.Option>
                                                                    ))}
                                                                </Select>
                                                            </Field>
                                                        </Col>

                                                        <Col md={6}>
                                                            <BaseField name={[condition.name, "resource"]}>
                                                                {({ value }) => {
                                                                    switch (value) {
                                                                        case "job": {
                                                                            return (
                                                                                <Field
                                                                                    name={[
                                                                                        condition.name,
                                                                                        "value",
                                                                                    ]}
                                                                                    label={t("jobs")}
                                                                                    rules={[
                                                                                        {
                                                                                            required: true,
                                                                                            message: t("value-required"),
                                                                                        },
                                                                                    ]}
                                                                                >
                                                                                    <ResourceSelect
                                                                                        resourcePath="/jobs"
                                                                                        mode="multiple"
                                                                                        renderLabel={renderSettingsResourceLabel}
                                                                                        getSearchFilters={getSettingsResourceSearchFilters}
                                                                                        placeholder={t("jobs")}
                                                                                        hasSearch
                                                                                    />
                                                                                </Field>
                                                                            );
                                                                        }
                                                                        case "location": {
                                                                            return (
                                                                                <Field
                                                                                    name={[
                                                                                        condition.name,
                                                                                        "value",
                                                                                    ]}
                                                                                    label={t("locations")}
                                                                                    rules={[
                                                                                        {
                                                                                            required: true,
                                                                                            message:
                                                                                                t(
                                                                                                    "value-required"
                                                                                                ),
                                                                                        },
                                                                                    ]}
                                                                                >
                                                                                    <ResourceSelect
                                                                                        resourcePath="/locations"
                                                                                        mode="multiple"
                                                                                        renderLabel={renderSettingsResourceLabel}
                                                                                        getSearchFilters={getSettingsResourceSearchFilters}
                                                                                        placeholder={t("locations")}
                                                                                        hasSearch
                                                                                    />
                                                                                </Field>
                                                                            );
                                                                        }
                                                                        case "union": {
                                                                            return (
                                                                                <Field
                                                                                    name={[
                                                                                        condition.name,
                                                                                        "value",
                                                                                    ]}
                                                                                    label={t("unions")}
                                                                                    rules={[
                                                                                        {
                                                                                            required: true,
                                                                                            message:
                                                                                                t(
                                                                                                    "value-required"
                                                                                                ),
                                                                                        },
                                                                                    ]}
                                                                                >
                                                                                    <ResourceSelect
                                                                                        resourcePath="/union"
                                                                                        mode="multiple"
                                                                                        renderLabel={renderSettingsResourceLabel}
                                                                                        getSearchFilters={getSettingsResourceSearchFilters}
                                                                                        placeholder={t("unions")}
                                                                                        hasSearch
                                                                                    />
                                                                                </Field>
                                                                            );
                                                                        }
                                                                        case "employee-type": {
                                                                            return (
                                                                                <Field
                                                                                    name={[
                                                                                        condition.name,
                                                                                        "value",
                                                                                    ]}
                                                                                    label={t("employee-types")}
                                                                                    rules={[
                                                                                        {
                                                                                            required: true,
                                                                                            message:
                                                                                                t(
                                                                                                    "value-required"
                                                                                                ),
                                                                                        },
                                                                                    ]}
                                                                                >
                                                                                    <ResourceSelect
                                                                                        resourcePath="/employee-type"
                                                                                        mode="multiple"
                                                                                        renderLabel={renderSettingsResourceLabel}
                                                                                        getSearchFilters={getSettingsResourceSearchFilters}
                                                                                        placeholder={t("employee-types")}
                                                                                        hasSearch
                                                                                    />
                                                                                </Field>
                                                                            );
                                                                        }
                                                                        case "employment-type": {
                                                                            return (
                                                                                <Field
                                                                                    name={[
                                                                                        condition.name,
                                                                                        "value",
                                                                                    ]}
                                                                                    label={t("employment-types")}
                                                                                    rules={[
                                                                                        {
                                                                                            required: true,
                                                                                            message:
                                                                                                t(
                                                                                                    "value-required"
                                                                                                ),
                                                                                        },
                                                                                    ]}
                                                                                >
                                                                                    <ResourceSelect
                                                                                        resourcePath="/user-demographic/employment-type"
                                                                                        mode="multiple"
                                                                                        renderLabel={renderSettingsResourceLabel}
                                                                                        getSearchFilters={getSettingsResourceSearchFilters}
                                                                                        placeholder={t("employment-types")}
                                                                                        hasSearch
                                                                                    />
                                                                                </Field>
                                                                            );
                                                                        }
                                                                        default: {
                                                                            const group = groups.find(
                                                                                (group) => group.level === value
                                                                            );
                                                                            if (!group) {
                                                                                break;
                                                                            }
                                                                            return (
                                                                                <Field
                                                                                    name={[
                                                                                        condition.name,
                                                                                        "value",
                                                                                    ]}
                                                                                    label={renderSettingsResourceLabel(group)}
                                                                                    rules={[
                                                                                        {
                                                                                            required: true,
                                                                                            message:
                                                                                                t(
                                                                                                    "value-required"
                                                                                                ),
                                                                                        },
                                                                                    ]}
                                                                                >
                                                                                    <ResourceSelect
                                                                                        resourcePath={`${group.id}/org-levels`}
                                                                                        mode="multiple"
                                                                                        renderLabel={
                                                                                            renderOrgLevelResourceLabel
                                                                                        }
                                                                                        getSearchFilters={getSettingsOrgResourceSearchFilters}
                                                                                        placeholder={t("levels")}
                                                                                        hasSearch
                                                                                    />
                                                                                </Field>
                                                                            );
                                                                        }
                                                                    }
                                                                }}
                                                            </BaseField>
                                                        </Col>

                                                        {conflictMap?.[ruleIndex]?.[conditionIndex] && (
                                                            <Col md={12} className="mb-4">
                                                                <span className="text-red">
                                                                    {t("condition-should-be-unique")}
                                                                </span>
                                                            </Col>
                                                        )}

                                                        <Col md={12}>
                                                            <Button
                                                                color="danger"
                                                                className="w-100 d-flex justify-content-center"
                                                                icon={<FontAwesomeIcon icon={faTrash} />}
                                                                onClick={() => removeCondition(condition.name)}
                                                            >
                                                                {t("remove-condition")}
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                </div>
                                            );
                                        })}

                                        <Row>
                                            <Col md={6}>
                                                <Button
                                                    className="w-100 d-flex justify-content-center"
                                                    icon={<FontAwesomeIcon icon={faPlus} />}
                                                    onClick={() => addCondition({ resource: "job" })}
                                                >
                                                    {t("add-condition")}
                                                </Button>
                                            </Col>

                                            <Col md={6}>
                                                <Button
                                                    color="danger"
                                                    className="w-100 d-flex justify-content-center"
                                                    icon={<FontAwesomeIcon icon={faTrash} />}
                                                    onClick={() => removeRule(rule.name)}
                                                >
                                                    {t("remove-rule")}
                                                </Button>
                                            </Col>
                                        </Row>
                                    </div>
                                );
                            }}
                        </List>
                    </Disclosure.Panel>
                </>
            )}
        </Disclosure>
    );
};

export default ShiftRules;
